import React, { useState } from 'react';
import { 
  Shield, 
  Heart, 
  FileText, 
  DollarSign, 
  Clock, 
  Calendar, 
  User, 
  Mail, 
  Phone, 
  CheckCircle, 
  Download,
  ArrowRight,
  X
} from 'lucide-react';

const FuneralTrustTemplate: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    bestTimeToContact: '',
    estimatedCostRange: '',
    paymentMethod: '',
    timeframe: '',
    agreeToTerms: false
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    if (!formData.bestTimeToContact) {
      newErrors.bestTimeToContact = 'Please select a preferred contact time';
    }
    
    if (!formData.estimatedCostRange) {
      newErrors.estimatedCostRange = 'Please select an estimated cost range';
    }
    
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Please select a preferred payment method';
    }
    
    if (!formData.timeframe) {
      newErrors.timeframe = 'Please select a timeframe';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the form data to your backend
      console.log('Form submitted:', formData);
      setSubmitted(true);
    }
  };

  // Function to handle going back to the form
  const handleBackToForm = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitted(false);
  };

  const benefits = [
    {
      icon: <Shield className="h-10 w-10 text-primary" />,
      title: "Asset Protection",
      description: "Protect your assets from probate and ensure they're distributed according to your wishes."
    },
    {
      icon: <DollarSign className="h-10 w-10 text-primary" />,
      title: "Tax Advantages",
      description: "Funeral trusts offer potential tax benefits and can help reduce the financial burden on your loved ones."
    },
    {
      icon: <Heart className="h-10 w-10 text-primary" />,
      title: "Peace of Mind",
      description: "Rest easy knowing your final arrangements are planned and funded, sparing your family difficult decisions."
    },
    {
      icon: <Clock className="h-10 w-10 text-primary" />,
      title: "Inflation Protection",
      description: "Lock in today's prices for future services, protecting against rising funeral costs."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50" style={{
      backgroundImage: 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0.7)), url(/pexels-dsnsyj-1139541.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {!submitted ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Plan Ahead with Dignity and
                <span className="block text-primary">Financial Wisdom</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8">
                A funeral trust is a thoughtful way to ensure your final wishes are honored while providing financial protection for you and your loved ones.
              </p>
              
              <div className="space-y-6 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {benefit.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">What is a Funeral Trust?</h3>
                <p className="text-blue-800">
                  A funeral trust is a legal arrangement designed to set aside funds for your funeral and final expenses. 
                  It provides financial protection, potential tax advantages, and ensures your wishes are carried out exactly as you planned.
                </p>
              </div>
              
              <div className="flex items-center">
                <button
                  onClick={() => window.open('/funeral-trust-guide.pdf', '_blank')}
                  className="flex items-center text-primary hover:text-primary-dark font-medium"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Our Free Guide to Funeral Trusts
                </button>
              </div>
            </div>
            
            {/* Right Column - Form */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Request Your Free Funeral Trust Consultation
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="John Smith"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="bestTimeToContact" className="block text-sm font-medium text-gray-700 mb-1">
                    Best Time to Contact <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Clock className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="bestTimeToContact"
                      name="bestTimeToContact"
                      value={formData.bestTimeToContact}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.bestTimeToContact ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a time</option>
                      <option value="morning">Morning (9AM - 12PM)</option>
                      <option value="afternoon">Afternoon (12PM - 5PM)</option>
                      <option value="evening">Evening (5PM - 8PM)</option>
                    </select>
                  </div>
                  {errors.bestTimeToContact && (
                    <p className="mt-2 text-sm text-red-600">{errors.bestTimeToContact}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="estimatedCostRange" className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Funeral Cost Range <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="estimatedCostRange"
                      name="estimatedCostRange"
                      value={formData.estimatedCostRange}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.estimatedCostRange ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a range</option>
                      <option value="under5k">Under $5,000</option>
                      <option value="5k-10k">$5,000 - $10,000</option>
                      <option value="10k-15k">$10,000 - $15,000</option>
                      <option value="15k-20k">$15,000 - $20,000</option>
                      <option value="over20k">Over $20,000</option>
                      <option value="unsure">Not sure yet</option>
                    </select>
                  </div>
                  {errors.estimatedCostRange && (
                    <p className="mt-2 text-sm text-red-600">{errors.estimatedCostRange}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="paymentMethod"
                      name="paymentMethod"
                      value={formData.paymentMethod}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.paymentMethod ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a method</option>
                      <option value="lumpSum">Lump Sum</option>
                      <option value="installments">Installments</option>
                      <option value="unsure">Not sure yet</option>
                    </select>
                  </div>
                  {errors.paymentMethod && (
                    <p className="mt-2 text-sm text-red-600">{errors.paymentMethod}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="timeframe" className="block text-sm font-medium text-gray-700 mb-1">
                    How Soon Do You Plan to Make Arrangements? <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Calendar className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="timeframe"
                      name="timeframe"
                      value={formData.timeframe}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.timeframe ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    >
                      <option value="">Select a timeframe</option>
                      <option value="immediately">Immediately</option>
                      <option value="1-3months">Within 1-3 months</option>
                      <option value="3-6months">Within 3-6 months</option>
                      <option value="6-12months">Within 6-12 months</option>
                      <option value="exploring">Just exploring options</option>
                    </select>
                  </div>
                  {errors.timeframe && (
                    <p className="mt-2 text-sm text-red-600">{errors.timeframe}</p>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary ${errors.agreeToTerms ? 'border-red-300' : ''}`}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                        I agree to be contacted about funeral trust services and understand my information will be kept confidential.
                      </label>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg text-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Request Free Consultation
                </button>
                
                <p className="text-center text-gray-500 text-sm mt-4">
                  Your information is protected by our <a href="#" className="underline">Privacy Policy</a>
                </p>
              </form>
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You</h2>
              <p className="text-xl text-gray-600 mb-6">
                Your request for a funeral trust consultation has been received. One of our advisors will contact you shortly at your preferred time to discuss your options.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">What to expect next:</h3>
                <ul className="text-left space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>A funeral trust specialist will call you within 24 hours</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>You'll receive a personalized analysis of your options</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>No obligation to proceed - this is an informational consultation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <span>You'll receive our comprehensive guide to funeral trusts</span>
                  </li>
                </ul>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => window.open('/funeral-trust-guide.pdf', '_blank')}
                  className="btn btn-primary flex items-center"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Download Your Free Guide Now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Trust Indicators Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Why Choose Our Funeral Trust Services</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Shield className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">FDIC Insured</h3>
              <p className="text-gray-600">Your funds are secure and protected up to $250,000</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Calendar className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">30+ Years Experience</h3>
              <p className="text-gray-600">Decades of experience in funeral planning and trust management</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <FileText className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Legally Compliant</h3>
              <p className="text-gray-600">All trusts are established in compliance with state and federal regulations</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                <Heart className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Compassionate Service</h3>
              <p className="text-gray-600">Guidance from advisors who understand the sensitivity of end-of-life planning</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* FAQ Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What is a funeral trust?</h3>
              <p className="text-gray-600">
                A funeral trust is a legal arrangement designed to set aside funds specifically for your funeral and final expenses. It allows you to plan ahead and ensure your wishes are carried out while potentially providing tax advantages and asset protection.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">What are the benefits of a funeral trust?</h3>
              <p className="text-gray-600">
                Funeral trusts offer several benefits, including asset protection, potential tax advantages, inflation protection by locking in today's prices, and peace of mind knowing your final arrangements are planned and funded, relieving your loved ones of this burden during a difficult time.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">How much should I set aside for funeral expenses?</h3>
              <p className="text-gray-600">
                The average funeral in the United States costs between $7,000 and $12,000, but this can vary widely based on your preferences and location. Our advisors can help you determine an appropriate amount based on your specific wishes and circumstances.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Is a funeral trust right for me?</h3>
              <p className="text-gray-600">
                Funeral trusts can be beneficial for many people, particularly those concerned about Medicaid eligibility, those who want to ensure their final wishes are carried out, or those who want to relieve their loved ones of financial burdens. Our free consultation can help determine if it's the right option for your situation.
              </p>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions about funeral trusts?</p>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="btn btn-primary inline-flex items-center"
            >
              Request a Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuneralTrustTemplate;